import axios from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import "../../Assests/Css/blog.css";
import arrowRight from "../../Assests/images/dark-right-arrow.png";
import wLogo from "../../Assests/images/w_logo.png";
import Skeleton from "../../Components/Common/SkeletonLoader/SkeletonLoader";
import { fetchCmsMetaData } from "../../Components/Utility/cmsMetaService";
import { SetMetaTags } from "../../Components/Utility/setMetaTags";

function BlogListing() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [gridPosts, setGridPosts] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [pageMeta, setPageMeta] = useState(null);
  const baseUrl = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_BASE?.replace("/api", "/");
  const [featuredImageError, setFeaturedImageError] = useState(false);
  const [gridImageErrors, setGridImageErrors] = useState({});
  const [meta, setMeta] = useState("");

  const currentGridPosts = gridPosts;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).toUpperCase();
    } catch (e) {
      return dateString;
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

    const fetchFeaturedBlog = async () => {
      try {
        setFeaturedLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/blogs/featured`);
        console.log("featuredRes", res.data)
        if (res.data && res.data.status) {
          setFeaturedPost(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching featured blog:", err);
      } finally {
        setFeaturedLoading(false);
      }
    };

    fetchFeaturedBlog();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setBlogsLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/blogs?page=${currentPage}`);
        if (res.data && res.data.status) {
          const apiData = res.data.data;
          setGridPosts(apiData.data || []);

          // Capture the page-level meta from the API (only on first load)
          if (currentPage === 1 && res.data.meta) {
            setPageMeta(res.data.meta);
          }

          if (apiData.total && apiData.per_page) {
            setTotalPages(Math.ceil(apiData.total / apiData.per_page) || 1);
          } else if (apiData.last_page) {
            setTotalPages(apiData.last_page);
          }
        }
      } catch (err) {
        console.error("Error fetching blogs:", err);
      } finally {
        setBlogsLoading(false);
      }
    };

    fetchBlogs();
  }, [currentPage]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.trim()) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    if (email.length >= 60) {
      setEmailError("Email address must not exceed 50 characters.");
      return;
    }
    if (!emailRegex.test(email.trim())) {
      setEmailError("Please enter a valid email address (e.g. name@example.com).");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE}/subscribe-now`, {
        email: email.trim(),
      });
      if (res.data && res.data.status) {
        toast.success(res.data.message);
        setEmail("");
        setEmailError("");
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.error("Error subscribing:", err);
      toast.error(
        err.response?.data?.message
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePageChangeScroll = () => {
    const element = document.getElementById("blog-grid-anchor");
    if (element) {
      const headerOffset = 120; // accounts for the sticky header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setTimeout(handlePageChangeScroll, 50);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setTimeout(handlePageChangeScroll, 50);
    }
  };

  useEffect(() => {
    const loadMeta = async () => {
      const data = await fetchCmsMetaData("blog");
      console.log("data,", data);
      setMeta(data);
    };
 
    loadMeta();
  }, []);

  const siteOrigin = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/+$/, "");

  return (
    <div className="journal-page">
      <SetMetaTags
        title={pageMeta?.title}
        description={pageMeta?.description}
        keywords={pageMeta?.keyword}
        canonicalUrl={`${siteOrigin}/blogs`}
        ogTitle={pageMeta?.title}
        ogDescription={pageMeta?.description}
        ogImage={`${siteOrigin}/images/blog-banner.png`}
        ogUrl={`${siteOrigin}/blogs`}
      />

      <div className="container">
        {/* Header Section */}
        <div className="journal-header">
          <span className="subtitle">{meta?.title}</span>
          <h1>{meta?.short_content}</h1>
          <div className="description" dangerouslySetInnerHTML={{ __html: meta.content }} />
        </div>

        {/* Featured Story Section - Common to all pages */}
        {featuredLoading ? (
          <div className="featured-card" style={{ backgroundImage: "none", backgroundColor: "#f4f5f6" }}>
            <div className="featured-overlay" style={{ background: "transparent" }}></div>
            <div className="featured-content">
              <Skeleton width="100px" height="24px" style={{ marginBottom: "15px" }} />
              <Skeleton width="60%" height="40px" style={{ marginBottom: "15px" }} />
              <Skeleton width="40%" height="40px" style={{ marginBottom: "20px" }} />
              <Skeleton width="80%" height="16px" style={{ marginBottom: "10px" }} />
              <Skeleton width="70%" height="16px" style={{ marginBottom: "30px" }} />
              <Skeleton width="120px" height="40px" />
            </div>
          </div>
        ) : featuredPost && (
          <div
            className="featured-card"
            style={featuredPost.hero_banner && !featuredImageError ? { backgroundImage: `url(${featuredPost.hero_banner.startsWith("http") ? featuredPost.hero_banner : baseUrl + featuredPost.hero_banner})` } : {}}
          >
            {featuredPost.hero_banner && !featuredImageError && (
              <img
                src={featuredPost.hero_banner.startsWith("http") ? featuredPost.hero_banner : baseUrl + featuredPost.hero_banner}
                alt={featuredPost.title || "Featured Blog Image"}
                title={featuredPost.title || "Featured Blog Image"}
                style={{ display: "none" }}
                onError={() => setFeaturedImageError(true)}
              />
            )}
            {(!featuredPost.hero_banner || featuredImageError) && (
              <div className="placeholder-logo-overlay">
                <img src={wLogo} alt="Weyfarin Logo" title="Weyfarin Logo" className="placeholder-logo" />
              </div>
            )}
            <div className="featured-overlay"></div>
            <div className="featured-content">
              <span className="featured-tag">{featuredPost.categories && featuredPost.categories.length > 0 ? featuredPost.categories[0].title : (featuredPost.tags ? featuredPost.tags.split(',')[0] : "Featured")}</span>
              <div className="feature-title">{featuredPost.title}</div>
              <p className="featured-description">{featuredPost.short_description}</p>
              <Link to={`/blogs/${featuredPost.slug}`} className="read-more-btn">
                Read More <img src={arrowRight} alt="Read More arrow" title="Read More arrow" />
              </Link>
            </div>
          </div>
        )}

        {/* Newsletter Dispatch Box */}
        <div className="dispatch-box">
          <div className="dispatch-text">
            <h3>Join our monthly dispatch</h3>
            <p>Curated travel stories and exclusive experiences delivered to your inbox.</p>
          </div>
          <form className="dispatch-form flex-wrap" onSubmit={handleSubscribe}>
            <input
              type="email"
              placeholder="Email address"
              value={email}
              maxLength={50}
              onChange={(e) => {
                const val = e.target.value;
                setEmail(val);
                if (!val) {
                  setEmailError("");
                } else if (val.length >= 60) {
                  setEmailError("Email address must not exceed 50 characters.");
                } else if (val.includes("@") && !emailRegex.test(val)) {
                  setEmailError("Please enter a valid email address (e.g. name@example.com).");
                } else {
                  setEmailError("");
                }
              }}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? "SUBSCRIBING..." : "SUBSCRIBE"}
            </button>
            {emailError && (
              <p className="text-danger small pl-2 text-start w-100">
                {emailError}
              </p>
            )}
          </form>
        </div>

        {/* Scroll anchor for pagination resets */}
        <div id="blog-grid-anchor"></div>

        {/* Blog Grid Section */}
        <div className="row">
          {blogsLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <div className="col-lg-4 col-md-6 col-sm-12 mb-4" key={index}>
                <div className="blog-grid-card">
                  <div className="blog-grid-image-wrapper" style={{ position: "relative" }}>
                    <Skeleton width="100%" height="100%" style={{ position: "absolute", top: 0, left: 0 }} />
                    <Skeleton
                      width="80px"
                      height="26px"
                      style={{
                        position: "absolute",
                        top: "20px",
                        left: "20px",
                        borderRadius: "8px",
                        zIndex: 2,
                      }}
                    />
                  </div>
                  <div style={{ padding: "15px 0" }}>
                    <Skeleton count={2} height="20px" style={{ marginTop: "10px" }} />
                    <Skeleton count={2} height="12px" style={{ marginTop: "10px" }} />
                    <Skeleton width="120px" height="12px" style={{ marginTop: "15px" }} />
                  </div>
                </div>
              </div>
            ))
          ) : currentGridPosts.length === 0 ? (
            <div className="col-12 text-center py-5">
              <p>No blog found.</p>
            </div>
          ) : (
            currentGridPosts.map((post) => {
              return (
                <div className="col-lg-4 col-md-6 col-sm-12" key={post.id}>
                  <Link to={`/blogs/${post.slug}`} className="blog-grid-card">
                    <div className="blog-grid-image-wrapper">
                      {Number(post.is_featured) === 1 && (
                        <div className="blog-image-overlay-tags-container">
                          <span className="blog-image-overlay-tag">
                            FEATURED
                          </span>
                        </div>
                      )}
                      {post.hero_banner && !gridImageErrors[post.id] ? (
                        <img
                          src={
                            post.hero_banner.startsWith("http")
                              ? post.hero_banner
                              : `${baseUrl}${post.hero_banner}`
                          }
                          alt={post.title}
                          title={post.title}
                          onError={() => {
                            setGridImageErrors((prev) => ({ ...prev, [post.id]: true }));
                          }}
                        />
                      ) : (
                        <div className="placeholder-image-wrapper">
                          <img src={wLogo} alt="Weyfarin Logo" title="Weyfarin Logo" className="placeholder-logo" />
                        </div>
                      )}
                    </div>
                    <h3 className="blog-card-title">{post.title}</h3>
                    <p className="blog-card-summary">
                      {post.short_description || post.description}
                    </p>
                    <div className="blog-card-footer">
                      {formatDate(post.publish_date)} &nbsp;•&nbsp; {post.min_read ? `${post.min_read} MIN READ` : "5 MIN READ"}
                    </div>
                  </Link>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Section - Shows only if total pages > 1 */}
        {!blogsLoading && totalPages > 1 && (
          <div className="blog-pagination">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <i className="fa fa-chevron-left" aria-hidden="true"></i>
            </button>

            <span className="pagination-info">
              {currentPage} / {totalPages}
            </span>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <i className="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogListing;
